import { useMutation } from "@tanstack/react-query";
import { QueryKeys, client, queryClient } from "queryClient";
import { CreateTaskTypeInput, InfiniteTaskTypeData } from "types";
import { getTaskType } from "modules/tasktypes/api";

export const useTaskTypeCreate = () => {
  const createMutation = useMutation({
    mutationFn: async (input: CreateTaskTypeInput) => {
      const { data: newTaskType } = await client.models.TaskType.create(input);

      if (!newTaskType) return null;

      const taskType = await getTaskType(newTaskType.id);

      return taskType;
    },
    // When mutate is called:
    onMutate: async (newTaskType) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [QueryKeys.TaskTypes] });

      // Snapshot the previous value
      const previousTaskTypes = queryClient.getQueryData([QueryKeys.TaskTypes]);

      // Optimistically update to the new value
      if (previousTaskTypes) {
        queryClient.setQueryData(
          [QueryKeys.TaskTypes],
          (old: InfiniteTaskTypeData) => {
            const oldData = old?.pages
              ? old.pages.flatMap((page) => page.taskTypeList)
              : [];

            [...oldData, newTaskType];
          },
        );
      }

      // Return a context object with the snapshotted value
      return { previousTaskTypes };
    },
    // If the mutation fails,
    // use the context returned from onMutate to rollback
    onError: (err, newTaskType, context) => {
      console.error("Error saving record:", err, newTaskType);
      if (context?.previousTaskTypes) {
        queryClient.setQueryData(
          [QueryKeys.TaskTypes],
          context.previousTaskTypes,
        );
      }
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.TaskTypes] });
    },
  });

  return createMutation;
};
